import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase, supabaseConfigured } from '../data/supabase';
import type { MemberRow, ModuleRole, OrgRow } from '../data/database.types';
import type { Grants } from './modules';
import type { Role } from './routes';

export type AuthStatus =
  | 'unconfigured'   // no VITE_SUPABASE_* in .env
  | 'loading'
  | 'signed-out'
  | 'recovery'       // arrived from a password-reset link; must set a new password
  | 'no-membership'  // signed in, but nobody invited this email
  | 'ready';

/** Passwords are checked in the database too; this is the friendly front door. */
export const MIN_PASSWORD = 8;

export interface AuthState {
  status: AuthStatus;
  session: Session | null;
  member: MemberRow | null;
  org: OrgRow | null;
  /** One role per module (D83). Portal admins read as admin in every module. */
  access: Grants;
  portalAdmin: boolean;
  /** The scheduler module's role in its legacy vocabulary, or null when the person has no access to it. */
  role: Role | null;
  error: string | null;
  signIn(email: string, password: string): Promise<string | null>;
  signUp(email: string, password: string): Promise<string | null>;
  sendPasswordReset(email: string): Promise<string | null>;
  updatePassword(password: string): Promise<string | null>;
  signInWithGoogle(): Promise<void>;
  signOut(): Promise<void>;
  refresh(): Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

const message = (e: unknown) => (e instanceof Error ? e.message : String(e));

const TO_LEGACY: Record<ModuleRole, Role> = { admin: 'admin', editor: 'scheduler', viewer: 'viewer' };

export function schedulerRoleOf(member: MemberRow | null, access: Grants): Role | null {
  if (!member) return null;
  if (member.portal_admin) return 'admin';
  const r = access.scheduler;
  return r ? TO_LEGACY[r] : null;
}

interface Membership {
  member: MemberRow;
  org: OrgRow;
  access: Grants;
}

async function loadMembership(): Promise<Membership | null> {
  const { data: userRes } = await supabase.auth.getUser();
  const uid = userRes.user?.id;
  if (!uid) return null;

  // Existing active membership first.
  const existing = await supabase
    .from('members')
    .select('*')
    .eq('user_id', uid)
    .eq('status', 'active')
    .order('created_at')
    .limit(1)
    .maybeSingle();
  if (existing.error) throw new Error(existing.error.message);

  let member: MemberRow | null = existing.data;
  if (!member) {
    // First sign-in: link the invitation that matches this email (spec 6.5).
    const claimed = await supabase.rpc('claim_membership');
    if (claimed.error) throw new Error(claimed.error.message);
    member = claimed.data && claimed.data.id ? claimed.data : null;
  }
  if (!member) return null;

  const [org, rows] = await Promise.all([
    supabase.from('orgs').select('*').eq('id', member.org_id).single(),
    supabase.from('member_module_access').select('module, role').eq('member_id', member.id),
  ]);
  if (org.error) throw new Error(org.error.message);
  if (rows.error) throw new Error(rows.error.message);
  const access: Grants = {};
  for (const r of rows.data ?? []) access[r.module] = r.role;
  return { member, org: org.data, access };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>(supabaseConfigured ? 'loading' : 'unconfigured');
  const [session, setSession] = useState<Session | null>(null);
  const [member, setMember] = useState<MemberRow | null>(null);
  const [org, setOrg] = useState<OrgRow | null>(null);
  const [access, setAccess] = useState<Grants>({});
  const [error, setError] = useState<string | null>(null);
  const [recovering, setRecovering] = useState(false);

  const resolve = useCallback(async (s: Session | null) => {
    setSession(s);
    if (!s) {
      setMember(null);
      setOrg(null);
      setAccess({});
      setStatus('signed-out');
      return;
    }
    try {
      const m = await loadMembership();
      if (!m) {
        setMember(null);
        setOrg(null);
        setAccess({});
        setStatus('no-membership');
      } else {
        setMember(m.member);
        setOrg(m.org);
        setAccess(m.access);
        setError(null);
        setStatus('ready');
      }
    } catch (e) {
      setError(message(e));
      setStatus('no-membership');
    }
  }, []);

  useEffect(() => {
    if (!supabaseConfigured) return;
    let cancelled = false;
    supabase.auth.getSession().then(({ data }) => {
      if (!cancelled) void resolve(data.session);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((event, s) => {
      if (cancelled) return;
      // A reset link signs the user in with a short-lived session whose only
      // purpose is setting a new password. Hold everything else until it is.
      if (event === 'PASSWORD_RECOVERY') {
        setSession(s);
        setRecovering(true);
        return;
      }
      if (event === 'SIGNED_IN' || event === 'SIGNED_OUT' || event === 'INITIAL_SESSION' || event === 'USER_UPDATED') {
        void resolve(s);
      } else {
        setSession(s);
      }
    });
    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, [resolve]);

  const signIn = useCallback(async (email: string, password: string) => {
    setError(null);
    const { error: err } = await supabase.auth.signInWithPassword({ email: email.trim().toLowerCase(), password });
    return err ? err.message : null;
  }, []);

  const signUp = useCallback(async (email: string, password: string) => {
    setError(null);
    const { data, error: err } = await supabase.auth.signUp({ email: email.trim().toLowerCase(), password });
    if (err) return err.message;
    // With confirmations off the account is usable at once. If an admin turns
    // them back on, signUp returns a user with no session; say so plainly
    // rather than leaving a blank screen.
    if (!data.session) return 'Account created. Check your email to confirm it before signing in.';
    return null;
  }, []);

  const sendPasswordReset = useCallback(async (email: string) => {
    setError(null);
    const { error: err } = await supabase.auth.resetPasswordForEmail(email.trim().toLowerCase(), {
      redirectTo: window.location.origin + '/',
    });
    return err ? err.message : null;
  }, []);

  const updatePassword = useCallback(async (password: string) => {
    setError(null);
    const { error: err } = await supabase.auth.updateUser({ password });
    if (err) return err.message;
    setRecovering(false);
    const { data } = await supabase.auth.getSession();
    await resolve(data.session);
    return null;
  }, [resolve]);

  const signInWithGoogle = useCallback(async () => {
    const { error: err } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: window.location.origin + '/' },
    });
    if (err) setError(err.message);
  }, []);

  const signOut = useCallback(async () => {
    setRecovering(false);
    await supabase.auth.signOut();
  }, []);

  const refresh = useCallback(async () => {
    const { data } = await supabase.auth.getSession();
    await resolve(data.session);
  }, [resolve]);

  const value = useMemo<AuthState>(
    () => ({
      status: recovering ? 'recovery' : status,
      session,
      member,
      org,
      access,
      portalAdmin: !!member?.portal_admin,
      role: schedulerRoleOf(member, access),
      error,
      signIn,
      signUp,
      sendPasswordReset,
      updatePassword,
      signInWithGoogle,
      signOut,
      refresh,
    }),
    [recovering, status, session, member, org, access, error, signIn, signUp, sendPasswordReset, updatePassword, signInWithGoogle, signOut, refresh]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}

/** The signed-in member and org, for portal pages that render once status is ready. */
export function usePortal(): { member: MemberRow; org: OrgRow; access: Grants; portalAdmin: boolean; role: Role | null } {
  const { member, org, access, portalAdmin, role } = useAuth();
  if (!member || !org) throw new Error('usePortal called before membership resolved');
  return { member, org, access, portalAdmin, role };
}

/** The signed-in member and org inside the scheduler module, where a scheduler role is guaranteed by the module gate. */
export function useMember(): { member: MemberRow; org: OrgRow; role: Role } {
  const { member, org, role } = useAuth();
  if (!member || !org || !role) throw new Error('useMember called outside the scheduler module');
  return { member, org, role };
}
